import { useCallback, useEffect, useState } from 'react'
import {
  Eye, RefreshCw, CheckCircle2, AlertCircle, EyeOff, Circle, ShieldAlert, Package,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import PortExposure from '../components/PortExposure'

interface Host {
  host: string
  prefix: string
  first_seen: string
  last_seen: string
  issuer: string
  cert_count: number
  status: 'known' | 'unknown' | 'boring' | 'ignored'
}

interface DomainBlock {
  domain: string
  subdomains: Host[]
  counts: Record<string, number>
  hibp: { count: number; breaches: any[] } | null
}

interface PackageBlock {
  ecosystem: string
  name: string
  vulns: any[]
}

interface ExposureData {
  domains: DomainBlock[]
  packages: PackageBlock[]
  hibp_enabled: boolean
  generated_at: string
}

const STATUS_STYLES: Record<string, { text: string; bg: string; icon: React.ElementType; label: string }> = {
  unknown: { text: 'text-rose-light',    bg: 'bg-rose/10',    icon: ShieldAlert, label: 'Unknown'  },
  known:   { text: 'text-emerald-light', bg: 'bg-emerald/10', icon: CheckCircle2, label: 'Known'   },
  boring:  { text: 'text-text-muted',    bg: 'bg-surface-hover', icon: Circle,    label: 'Standard' },
  ignored: { text: 'text-text-muted',    bg: 'bg-surface-hover', icon: EyeOff,    label: 'Ignored'  },
}

export default function ExposurePage() {
  const [data, setData] = useState<ExposureData | null>(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<'all' | 'unknown' | 'known' | 'boring' | 'ignored'>('all')
  const [domainInput, setDomainInput] = useState('')
  const [domainList, setDomainList] = useState<{ env: string[]; runtime: string[]; effective: string[] } | null>(null)

  const fetchDomains = useCallback(async () => {
    try {
      const r = await api.get('/api/cyber/exposure/domains')
      setDomainList(r.data)
    } catch {}
  }, [])

  useEffect(() => { fetchDomains() }, [fetchDomains])

  const addDomain = async () => {
    const d = domainInput.trim().toLowerCase()
    if (!d) return
    try {
      await api.post('/api/cyber/exposure/domains', null, { params: { domain: d } })
      setDomainInput('')
      await fetchDomains()
      await fetchData()
    } catch (e: any) {
      alert(`Couldn't add domain: ${e?.response?.data?.detail || e?.message}`)
    }
  }

  const removeDomain = async (d: string) => {
    try {
      await api.delete(`/api/cyber/exposure/domains/${encodeURIComponent(d)}`)
      await fetchDomains()
      await fetchData()
    } catch {}
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/exposure')
      setData(r.data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const markHost = async (host: string, status: 'known' | 'ignored' | 'unknown') => {
    try {
      await api.put(`/api/cyber/exposure/host/${encodeURIComponent(host)}?status=${status}`)
      await fetchData()
    } catch {}
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Eye size={24} className="text-blue" /> External Exposure
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            CT-log subdomain discovery · HIBP breach lookup · OSV.dev supply-chain advisories
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Domain manager — always visible so users can add/remove without shell access */}
      {domainList && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-text-primary">Monitored domains</h2>
            <span className="text-[10px] text-text-muted">
              {domainList.env.length} env · {domainList.runtime.length} runtime · {domainList.effective.length} total
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {domainList.env.map(d => (
              <span key={`env-${d}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-hover text-text-secondary text-[10px] border border-border">
                <span className="font-mono">{d}</span>
                <span className="text-text-muted">(env)</span>
              </span>
            ))}
            {domainList.runtime.map(d => (
              <span key={`rt-${d}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue/10 text-blue-light text-[10px] border border-blue/30">
                <span className="font-mono">{d}</span>
                <button type="button" onClick={() => removeDomain(d)} className="text-text-muted hover:text-rose" title="Remove">×</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              value={domainInput}
              onChange={e => setDomainInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addDomain() }}
              placeholder="example.com"
              className="input py-1.5 text-xs flex-1 max-w-sm font-mono"
            />
            <button type="button" onClick={addDomain} className="btn-secondary text-xs">Add domain</button>
          </div>
          <p className="text-[10px] text-text-muted mt-1">
            Env-sourced domains (from <code>MONITORED_DOMAINS</code>) can only be changed in backend/.env.
            Runtime domains are editable here and persist in <code>/app/data/monitored_domains.json</code>.
          </p>
        </div>
      )}

      {!data?.domains?.length && !loading && (
        <div className="card p-6 text-center text-text-muted text-sm">
          No domains configured. Add one above or set <code className="text-text-secondary">MONITORED_DOMAINS=yourorg.com</code> in backend/.env.
        </div>
      )}

      {/* Censys-style port exposure via Shodan InternetDB (free, no scan from us) */}
      <PortExposure />

      {data?.domains?.map(d => {
        const total = d.subdomains.length
        const unk = d.counts.unknown || 0
        const filtered = d.subdomains.filter(h => statusFilter === 'all' || h.status === statusFilter)
        return (
          <div key={d.domain} className="card p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h2 className="text-sm font-bold text-text-primary font-mono">{d.domain}</h2>
                <p className="text-[11px] text-text-muted mt-0.5">
                  {total} unique subdomain{total === 1 ? '' : 's'} observed in CT logs · {unk} flagged for review
                </p>
              </div>
              <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[10px]">
                {(['all', 'unknown', 'known', 'boring', 'ignored'] as const).map(s => {
                  const style = s === 'all' ? null : STATUS_STYLES[s]
                  const n = s === 'all' ? total : (d.counts[s] || 0)
                  const active = statusFilter === s
                  return (
                    <button key={s} type="button" onClick={() => setStatusFilter(s)}
                      className={clsx('px-2 py-1 capitalize',
                        active
                          ? style?.bg + ' ' + (style?.text ?? 'text-text-primary') + ' font-semibold'
                          : 'text-text-muted hover:bg-surface-hover')}>
                      {s} <span className="font-mono text-[9px] opacity-70">({n})</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {d.hibp !== null && (
              <div className={clsx(
                'mb-3 p-2 rounded border text-xs',
                (d.hibp.count || 0) > 0 ? 'border-rose/30 bg-rose/5 text-rose-light' : 'border-emerald/30 bg-emerald/5 text-emerald-light',
              )}>
                <AlertCircle size={11} className="inline mr-1" />
                HIBP: {(d.hibp.count || 0) > 0
                  ? <>{d.hibp.count} breach{d.hibp.count === 1 ? '' : 'es'} expose this domain — {(d.hibp.breaches || []).slice(0,3).map((b: any) => b.title).join(', ')}</>
                  : <>no public breaches recorded against this domain</>}
              </div>
            )}
            {d.hibp === null && data?.hibp_enabled === false && (
              <div className="mb-3 p-2 rounded border border-border bg-surface-hover text-[10px] text-text-muted">
                Set <code>HIBP_API_KEY</code> in <code>backend/.env</code> to enable domain-level breach lookup.
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-text-muted">
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Host</th>
                    <th className="text-left py-2 px-2">First seen</th>
                    <th className="text-left py-2 px-2">Latest cert expiry</th>
                    <th className="text-right py-2 px-2">Certs</th>
                    <th className="text-left py-2 px-2">Issuer</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(h => {
                    const s = STATUS_STYLES[h.status]
                    const Icon = s.icon
                    return (
                      <tr key={h.host} className="border-b border-border/50 hover:bg-surface-hover">
                        <td className="py-1.5 px-2">
                          <span className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]', s.bg, s.text)}>
                            <Icon size={9} /> {s.label}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 font-mono text-text-primary">{h.host}</td>
                        <td className="py-1.5 px-2 text-text-muted font-mono text-[10px]">{(h.first_seen || '').slice(0, 10)}</td>
                        <td className="py-1.5 px-2 text-text-muted font-mono text-[10px]">{(h.last_seen || '').slice(0, 10)}</td>
                        <td className="py-1.5 px-2 text-right text-text-muted font-mono">{h.cert_count}</td>
                        <td className="py-1.5 px-2 text-text-muted text-[10px] truncate max-w-[200px]" title={h.issuer}>
                          {h.issuer?.split(',').find((p: string) => p.trim().startsWith('O=')) || h.issuer}
                        </td>
                        <td className="py-1.5 px-2 text-right space-x-1 whitespace-nowrap">
                          {h.status !== 'known' && (
                            <button type="button" onClick={() => markHost(h.host, 'known')}
                              className="text-[10px] text-emerald hover:underline">mark known</button>
                          )}
                          {h.status !== 'ignored' && (
                            <button type="button" onClick={() => markHost(h.host, 'ignored')}
                              className="text-[10px] text-text-muted hover:underline">ignore</button>
                          )}
                          {(h.status === 'known' || h.status === 'ignored') && (
                            <button type="button" onClick={() => markHost(h.host, 'unknown')}
                              className="text-[10px] text-rose-light hover:underline">reset</button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-text-muted">No hosts in this state.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}

      {data?.packages && data.packages.length > 0 && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <Package size={14} className="text-amber" /> Watched packages · OSV.dev advisories
          </h2>
          <div className="space-y-3">
            {data.packages.map(p => (
              <div key={`${p.ecosystem}:${p.name}`} className="border border-border rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-xs text-text-primary">{p.ecosystem}:{p.name}</div>
                  <span className={clsx('text-[10px] px-2 py-0.5 rounded-full',
                    p.vulns.length > 0 ? 'bg-rose/10 text-rose-light' : 'bg-emerald/10 text-emerald-light')}>
                    {p.vulns.length} advisor{p.vulns.length === 1 ? 'y' : 'ies'}
                  </span>
                </div>
                {p.vulns.slice(0, 5).map(v => (
                  <div key={v.id} className="text-[11px] py-1 border-t border-border/50">
                    <span className="font-mono text-blue-light">{v.id}</span>
                    {v.severity && <span className="text-text-muted ml-2">CVSS {v.severity}</span>}
                    <div className="text-text-secondary mt-0.5">{v.summary}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
